//import sha1 from './sha1';
import {isHeic, heicTo} from 'heic-to';
import generateThumbnail from './thumbnail';

/**
 * @description
 * Promise rejection keys for various failed parsing states
 *
 * @type {{INVALID: string, EXIF: string, READ: string, SHA1: string}}
 */
export const REJECTION_KEYS = {
  INVALID: 'INVALID_IMAGE_ERROR',
  IMAGE_READ: 'IMAGE_READ_ERROR',
  FILE_READ: 'FILE_READ_ERROR'
};

/**
 * @name ImageProcessor
 * @description
 * Handles various jobs around processing an image for upload
 */
export default class ImageProcessor {

  constructor(file, options = {}) {

    //validity check
    this.isValidImage = options.skipValidation ? true : isValidImage(file);

    //basic file props
    this.file = file;
    this.name = file.name;
    this.type = file.type;
    this.size = {
      bytes: file.size,
      kbytes: `${Math.round(file.size / 1024)}KB`
    };

    //generated thumb
    this.thumbnail = null;
    this.thumbWidth = options.thumbWidth || 150;
    this.thumbHeight = options.thumbHeight || 150;
    this.thumbQuality = options.thumbQuality || 0.75;

    //set date initially to last modified
    this.takenAt = getFileLastModifiedDate(this.file);

    //read file to array buffer so it can be shared across methods
    this.arrayBufferPromise = new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(Error(REJECTION_KEYS.FILE_READ));
      reader.readAsArrayBuffer(file);
    });

    //note: incomment this if you want heic processing
    this.heicBlobPromise = ''; /*new Promise(async (resolve, reject) => {
      try {
        if (await isHeic(this.file)) {
          const jpeg = await heicTo({
            blob: this.file,
            type: 'image/jpeg'
          });
          resolve(jpeg);
        }
      } catch (e) {
        reject(e);
      }
    });*/
  }

  async getDimensions() {
    if (!this.isValidImage) {
      throw Error(REJECTION_KEYS.INVALID);
    }

    // webkit6 uses prefixed version
    const windowURL = window.URL || window.webkitURL;
    const buffer = await this.arrayBufferPromise;

    return new Promise(async (resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        this.width = image.width;
        this.height = image.height;
        resolve(this);

        if (windowURL) {
          windowURL.revokeObjectURL(image.src);
        }
      };

      image.onerror = () => {
        reject(Error(REJECTION_KEYS.READ));
      };

      if (await isHeic(this.file)) {
        image.src = windowURL.createObjectURL(await this.heicBlobPromise);
      } else {
        image.src = windowURL ? windowURL.createObjectURL(this.file) : buffer;
      }
    });
  }

  async getOrientation() {
    if (!this.isValidImage) {
      throw Error(REJECTION_KEYS.INVALID);
    }

    const buffer = await this.arrayBufferPromise;
    const {Orientation} = getOrientationFromExif(buffer);
    this.orientation = Orientation || 1;
    return this;
  }

  async getSHA1() {
    const arrayBuffer = await this.file.arrayBuffer();
    const hash = await window.crypto.subtle.digest('SHA-1', arrayBuffer);
    this.sha1 = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
    return this;
  }

  async generateThumb() {
    if (!this.isValidImage) {
      throw Error(REJECTION_KEYS.INVALID);
    }

    const buffer = await this.arrayBufferPromise;
    const heicBlob = (await isHeic(this.file)) ? await this.heicBlobPromise : null;
    this.thumbnail = await generateThumbnail(this.file, buffer, heicBlob, this.thumbWidth, this.thumbHeight, this.orientation, this.thumbQuality);
    return this;
  }

  async process() {
    await this.arrayBufferPromise;
    await this.getDimensions();

    if (!(await isHeic(this.file))) {
      await this.getOrientation();
    }

    await this.generateThumb();
    return this;
  }

  destroyBuffer() {
    this.arrayBufferPromise = null;
  }
}

function getOrientationFromExif(buffer) {
  const result = {};
  const slicedBuffer = buffer.slice(0, 80000);
  const view = new DataView(slicedBuffer);

  if (view.getUint16(0, false) !== 0xFFD8) {
    return result;
  }

  const length = view.byteLength;
  let offset = 2;
  while (offset < length) {
    const marker = view.getUint16(offset, false);
    offset += 2;
    if (marker === 0xFFE1) {
      if (view.getUint32(offset += 2, false) !== 0x45786966) {// eslint-disable-line
        return result;
      }
      const little = view.getUint16(offset += 6, false) === 0x4949;
      offset += view.getUint32(offset + 4, little);
      const tags = view.getUint16(offset, little);
      offset += 2;
      for (let i = 0; i < tags; i++) {

        if (view.getUint16(offset + (i * 12), little) === 0x0112) {
          result.Orientation = view.getUint16(offset + (i * 12) + 8, little);
        }

      }
    } else if ((marker & 0xFF00) !== 0xFF00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }
  return result;
}
